import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo
} from "react";
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import Toaster from "../../stories/Toasts/Toaster";
import { useLocation, useNavigate } from "react-router-dom";

// Creates AuthContext for managing authentication and authorization
const AuthContext = createContext();

// Provider component to manage authentication and authorization state
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isLoggedIn") === "true"
  );
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [accessToken, setAccessToken] = useState(
    Cookies.get("access_token") || localStorage.getItem("access_token") || ""
  );
  const [refreshToken, setRefreshToken] = useState(
    Cookies.get("refresh_token") || localStorage.getItem("refresh_token") || ""
  );
  const [user, setUser] = useState(localStorage.getItem("user_id"));
  const [showWarning, setShowWarning] = useState(false);
  const [userInitials, setUserInitials] = useState("guest");
  const [toast, setToast] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(1);
  const [convertValues, setConvertValues] = useState(false);
  const [unitType, setUnitType] = useState("metric");
  const [toggled, setToggled] = useState(false);
  const [isNonUKNumber, setIsNonUKNumber] = useState(false);
  const [primaryPhoneNumber, setPrimaryPhoneNumber] = useState(false);
  const [countrySelected, setCountrySelected] = useState("");
  const [nextURl, setNextURl] = useState();
  const [editReload, setEditReload] = useState(false);
  const [step, setStep] = useState(completedSteps);
  const [isCompleted, setIsCompleted] = useState(0);
  const [allStepData, setAllStepData] = useState();
  const [kitId, setKitId] = useState();
  const [stepId, setstepId] = useState();
  const [totalSteps, setTotalSteps] = useState(4);
  const [isHcpLive, setIsHcpLive] = useState(localStorage.getItem('isHcpLive'));
  const [isPkbLive, setIsPkbLive] = useState(localStorage.getItem('isPkbLive'));
  const [progressSteps, setProgressSteps] = useState({
    totalSteps: 4,
    currentStep: 1,
    completedSteps: 0
  });

  const location = useLocation();
  const isPhlebotomyTest = location.pathname.includes('boots')

  const showToast = (header, body, image, icon) => {
    const Icon = icon;
    setToast({
      header: header,
      body: body,
      imgSrc: image,
      icon: Icon
    });
  };
  const navigate = useNavigate();
  useEffect(() => {
    setstepId(location.pathname.split('/').at(-1))
    setKitId(location.pathname.split('/').at(-2))
  }, [location?.pathname])
  const handleNextStep = useCallback(() => {
    setIsCompleted(1); // Increment the step based on the latest value
    setCompletedSteps(prevSteps => prevSteps + 1); // Use functional update to access the latest value
    setStep(prevStep => prevStep + 1);
  }, []);

  const handlePreviousStep = useCallback((test_kit_id, type, isAssessmentCompletedForPhlebotomy) => {
    setCompletedSteps(prevSteps => prevSteps - 1); // Use functional update to access the latest value
    setStep(prevStep => prevStep - 1);
    const pathSegments = location.pathname.split('/').filter(Boolean); // Filter out empty segments

    if (isPhlebotomyTest) {
      // Ensure the required path segments are available
      const stepNumber = Number.parseInt(pathSegments.at(-1)) || 1; // Default to 1 if undefined
      const bid = pathSegments.at(-2) || ''; // Provide a fallback value
      const test_type = pathSegments.at(-3) || ''; // Provide a fallback value
      if (stepNumber <= 1) {
        const query = isAssessmentCompletedForPhlebotomy
          ? "?edit=true"
          : "";
        navigate(
          `/bloods/boots/${test_type}/booking/confirm-details/${bid}${query}`
        );
        return;
      }

      navigate(`/bloods/boots/assessment/${test_type}/${bid}/${stepNumber - 1}`);
    } else {
      navigate(`/assessment/${kitId || test_kit_id}/${Number.parseInt(stepId) - 1}`)

    }
  }, [kitId, stepId]);

  // Function to set JWT tokens
  const setTokens = (tokens, email, role) => {
    if (tokens) {
      // Store access and refresh tokens in cookies
      Cookies.set("access_token", tokens.access_token, {
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict', // Prevents CSRF
        expires: 2 / 24 // 2 hour expiration
      });

      Cookies.set("refresh_token", tokens.refresh_token, {
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict',
        expires: 30 // 30 days expiration
      });
      sessionStorage.setItem("email", email);
      localStorage.setItem("role", role);
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);
      setEmail(email);
      setRole(role);
    }
  };
  // Inactivity and warning time constants
  const INACTIVITY_TIME = 2 * 60 * 60 * 1000;
  const WARNING_TIME = 5 * 60 * 1000;
  let warningTimeout;
  let logoutTimeout;

  // Reset inactivity timers
  const resetTimers = useCallback(() => {
    clearTimeout(warningTimeout);
    clearTimeout(logoutTimeout);

    if (isAuthenticated) {
      warningTimeout = setTimeout(() => {
        setShowWarning(true);
      }, INACTIVITY_TIME);

      logoutTimeout = setTimeout(() => {
        setShowWarning(false);
        logout();
      }, INACTIVITY_TIME + WARNING_TIME);
    }
  }, [isAuthenticated]);

  // Handle user activity to reset timers
  const handleUserActivity = useCallback(() => {
    resetTimers();
    setShowWarning(false);
  }, [resetTimers]);

  // Effect to add/remove event listeners for user activity
  useEffect(() => {
    globalThis.addEventListener("mousemove", handleUserActivity);
    globalThis.addEventListener("keydown", handleUserActivity);

    if (isAuthenticated) {
      resetTimers();
    }

    return () => {
      globalThis.removeEventListener("mousemove", handleUserActivity);
      globalThis.removeEventListener("keydown", handleUserActivity);
    };
  }, [isAuthenticated, handleUserActivity, resetTimers]);

  // For dynamic rendering of User initials in profile icon
  useEffect(() => {
    setUserInitials(localStorage.getItem("username") || 'guest');
  }, [isAuthenticated]);

  // Login function to set authentication state
  const login = data => {
      !data.mfa_enabled && localStorage.setItem("isLoggedIn", "true");
    // Store tokens in cookies
    Cookies.set("access_token", data.access_token, {
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
      expires: 2 / 24 // 2 hour expiration
    });

    Cookies.set("refresh_token", data.refresh_token, {
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
      expires: 30 // 30 days expiration
    });
    sessionStorage.setItem("email", data.email);
    localStorage.setItem("username", data.fullname);
    localStorage.setItem("mfa_enabled", data.mfa_enabled || false);
    data.id && localStorage.setItem("user_id", data.id);
    setRole(data.role);
    setAccessToken(
      data.access_token
        ? data.access_token
        : Cookies.get("access_token")
    );
    setRefreshToken(
      data.refresh_token
        ? data.refresh_token
        : Cookies.get("refresh_token")
    );
    if(data.id){
       setUser(data.id);
    }
    setTimeout(() => {
      setIsAuthenticated(localStorage.getItem("isLoggedIn") === "true");
      resetTimers();
    }, 500);
  };

  // Logout function to clear authentication state
  const logout = () => {
    setIsAuthenticated(false);
    // Remove tokens from cookies
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");

    // Clear the next URL to prevent redirecting to previous user's pages
    setNextURl("");

    localStorage.clear();
    clearTimeout(warningTimeout);
    clearTimeout(logoutTimeout);
  };

  // Effect to handle changes in local storage
  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(localStorage.getItem("isLoggedIn") === "true");
      // Check both cookies and localStorage for tokens
      setAccessToken(Cookies.get("access_token") || localStorage.getItem("access_token") || "");
      setRefreshToken(Cookies.get("refresh_token") || localStorage.getItem("refresh_token") || "");
      setUser(localStorage.getItem("user_id"));
      setRole(localStorage.getItem("role"));
      setEmail(sessionStorage.getItem("email"));
    };

    globalThis.addEventListener("storage", handleStorageChange);

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
    };
  }, []);


  const contextValue = useMemo(() => ({
    isAuthenticated,
    setIsAuthenticated,
    showToast,
    toast,
    setToast,
    login,
    logout,
    role,
    setTokens,
    user,
    updateState: setUser,
    setTitle,
    title,
    email,
    setEmail,
    accessToken,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    totalSteps,
    setTotalSteps,
    completedSteps,
    setCompletedSteps,
    showWarning,
    setShowWarning,
    userInitials,
    convertValues,
    setConvertValues,
    unitType,
    setUnitType,
    setUserInitials,
    toggled,
    setToggled,
    isNonUKNumber,
    setIsNonUKNumber,
    primaryPhoneNumber,
    setPrimaryPhoneNumber,
    countrySelected,
    setCountrySelected,
    nextURl,
    setNextURl,
    editReload,
    setEditReload,
    step,
    setStep,
    isCompleted,
    setIsCompleted,
    handleNextStep,
    handlePreviousStep,
    allStepData,
    setAllStepData,
    isHcpLive,
    setIsHcpLive,
    isPkbLive,
    setIsPkbLive,
    progressSteps,
    setProgressSteps
  }), [
    isAuthenticated,
    showToast,
    toast,
    role,
    user,
    title,
    email,
    totalSteps,
    setTotalSteps,
    accessToken,
    refreshToken,
    completedSteps,
    showWarning,
    setShowWarning,
    userInitials,
    setUserInitials,
    convertValues,
    unitType,
    toggled,
    isNonUKNumber,
    primaryPhoneNumber,
    countrySelected,
    nextURl,
    setNextURl,
    editReload,
    step,
    setStep,
    handleNextStep,
    isHcpLive,
    setIsHcpLive,
    isCompleted,
    setIsCompleted,
    handlePreviousStep,
    allStepData,
    setAllStepData,
    isPkbLive,
    setIsPkbLive,
    progressSteps,
    setProgressSteps
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {toast && (
        <Toaster
          header={toast.header}
          body={toast.body}
          imgSrc={toast.imgSrc}
          icon={toast.icon}
          autoHideDuration={3000}
          onClose={() => setToast(null)}
          newToast={toast}
        />
      )}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export const useAuth = () => useContext(AuthContext);
